import { chakra, forwardRef, useMultiStyleConfig } from '@chakra-ui/react-v2';
import { cx } from '@chakra-ui/utils';

import {
  type BoxProps,
  Center,
  Spinner,
  useFileContext,
} from '@frontend/legacy-components';

const READ_ONLY_STYLES = {
  backgroundColor: 'page.surface',
  borderColor: 'separator.border',
  color: 'feature.primary',
};

export type FileInlineCardProps = BoxProps & {
  isManuallyActive?: boolean;
};

/**
 * Renders an inline representation of a file.
 *
 * Intended to be composed with `FileInlineCardIcon` and `FileInlineCardLabel`. Must be wrapped in `FileProvider`.
 */
export const FileInlineCard = forwardRef<FileInlineCardProps, 'div'>(
  ({ isManuallyActive, className, children, ...restProps }, ref) => {
    const { filename, highlight, isDisabled, isLoading, isReadOnly, size } =
      useFileContext();
    const shouldHighlight = !!highlight && filename.includes(highlight);

    const styles = useMultiStyleConfig('FileInlineCard', { size });

    return (
      <chakra.div
        ref={ref}
        className={cx('file-inline-card', className)}
        __css={styles.container}
        {...(shouldHighlight && { borderColor: 'brand.secondary' })}
        {...restProps}
        {...((isDisabled || isLoading) && { disabled: true })}
        {...(isReadOnly && {
          ...READ_ONLY_STYLES,
          _hover: READ_ONLY_STYLES,
          _active: READ_ONLY_STYLES,
        })}
        {...(isManuallyActive && {
          backgroundColor: 'interaction.surfaceActive',
          borderColor: 'interaction.active',
          color: 'interaction.active',
        })}
      >
        {children}
        {isLoading && (
          <Center paddingLeft={0.5}>
            <Spinner size="sm" />
          </Center>
        )}
      </chakra.div>
    );
  },
);
